import { Alert, DatePicker, InputNumber, Radio, Select, Space } from "antd"
import VSpace from "../../common/VSpace"
import DayOfWeekSelector from "./DayOfWeekSelector"
import * as PaymentScheduleModel from "../paymentScheduleModel"
import dayjs from 'dayjs'
import RecurringPaymentSummary from "./RecurringPaymentSummary"
import { InvoiceEveryUnit, RecurringPayment } from "../../../domain/proposals/settings/proposalSettings"
import { hasValue } from "../../../helpers/nullable"

type Props = {
  recurringPayment: RecurringPayment
  totalAmount?: number
  totalPayments?: number
  showEstimated?: boolean
  onChange: (value: RecurringPayment) => void
  hideEstimatedStartDate?: boolean
}

const dateDisplayFormat = 'MMM D, YYYY'

const InvoiceRateSelector = ({recurringPayment, totalAmount, totalPayments, showEstimated, onChange, hideEstimatedStartDate}: Props) => {
  const perInvoiceAmount = totalPayments && hasValue(totalAmount)
    ? Math.round(totalAmount / totalPayments)
    : undefined

  return <VSpace>
    {!hideEstimatedStartDate && <Space>
      Estimated start date
      <DatePicker
        value={recurringPayment.estimatedStartDate ? dayjs(recurringPayment.estimatedStartDate) : undefined}
        onChange={value => onChange(PaymentScheduleModel.updateRecurringEstimatedStartDate(recurringPayment, value?.toISOString() ?? null))}
        minDate={dayjs()}
        format={dateDisplayFormat}
      />
    </Space>}
    <Space>
      Invoice every
      <InputNumber
        value={recurringPayment.invoiceEvery}
        min={1}
        max={36}
        onChange={value => onChange(PaymentScheduleModel.updateRecurringInvoiceEvery(recurringPayment, value ?? 1))}
      />
      <Select
        value={recurringPayment.invoiceEveryUnit}
        options={[
          { label: 'week(s)', value: 'weeks' },
          { label: 'month(s)', value: 'months' },
        ]}
        onChange={value => onChange(PaymentScheduleModel.updateRecurringInvoiceEveryUnit(recurringPayment, value as InvoiceEveryUnit))}
      />
    </Space>
    {recurringPayment.invoiceEveryUnit === 'weeks' && <VSpace size="small">
      <span>Repeat on</span>
      <DayOfWeekSelector
        value={recurringPayment.weeklyRepeatOn}
        onChange={value => onChange(PaymentScheduleModel.updateRecurringWeeklyRepeatOn(recurringPayment, value))}
      />
    </VSpace>}
    {recurringPayment.invoiceEveryUnit === 'months' && <>
      <Space>
        <Radio
          checked={recurringPayment.monthlyOption === 'day'}
          onClick={() => onChange(PaymentScheduleModel.updateRecurringMonthlyOption(recurringPayment, 'day'))}
        >
          Repeat monthly on day
        </Radio>
        <InputNumber
          value={recurringPayment.monthlyRepeatOnDay}
          min={1}
          max={28}
          onChange={value => onChange(PaymentScheduleModel.updateRecurringMonthlyRepeatOnDay(recurringPayment, value ?? 1))}
          disabled={recurringPayment.monthlyOption !== 'day'}
        />
      </Space>
      <VSpace size="small">
        <div>
          <Radio
            checked={recurringPayment.monthlyOption === 'nth-week'}
            onClick={() => onChange(PaymentScheduleModel.updateRecurringMonthlyOption(recurringPayment, 'nth-week'))}
          >
            Repeat monthly on every
          </Radio>
          <Select
            value={recurringPayment.monthlyRepeatEveryNthWeek}
            options={[
              { label: 'first', value: 'first' },
              { label: 'second', value: 'second' },
              { label: 'third', value: 'third' },
              { label: 'fourth', value: 'fourth' }
            ]}
            popupMatchSelectWidth={false}
            onChange={value => onChange(PaymentScheduleModel.updateRecurringMonthlyRepeatEveryNthWeek(recurringPayment, value))}
            disabled={recurringPayment.monthlyOption !== 'nth-week'}
          />
        </div>
        <div style={{ marginLeft: '24px' }}>
          <DayOfWeekSelector
            value={recurringPayment.monthlyRepeatOn}
            onChange={value => onChange(PaymentScheduleModel.updateRecurringMonthlyRepeatOn(recurringPayment, value))}
            disabled={recurringPayment.monthlyOption !== 'nth-week'}
          />
        </div>
      </VSpace>
    </>}

    <Alert type="info" message={<RecurringPaymentSummary
      recurringPayment={recurringPayment}
      totalPayments={totalPayments}
      perInvoiceAmount={perInvoiceAmount}
      showEstimated={showEstimated}
      onHideScheduleChange={value => onChange(PaymentScheduleModel.updateRecurringHideScheduleOnProposal(recurringPayment, value))}
    />} />
  </VSpace>
}

export default InvoiceRateSelector

